/**
 * MySQL JSON-path extraction from cursor context.
 *
 * Given the buffer text and a cursor offset inside a comparison RHS string
 * literal (`WHERE metadata->>'$.status' = '|'`), detects whether the LHS of
 * the comparison is a MySQL JSON-extract expression and returns the
 * normalized canonical JSONPath (`$.status`) so the enum-value resolver can
 * look up path-keyed hints.
 *
 * Supported MySQL JSON forms (v1):
 *   col->>'$.a.b'                          → canonical `$.a.b`
 *   `col`->>'$.a.b'                        → canonical `$.a.b`  (backtick-quoted column)
 *   JSON_EXTRACT(col, '$.a.b')             → canonical `$.a.b`
 *   JSON_VALUE(col, '$.a.b')               → canonical `$.a.b`  (MySQL 8.0.21+)
 *   JSON_UNQUOTE(JSON_EXTRACT(col, '$.a.b')) → canonical `$.a.b`
 *
 * **MySQL path literal MUST be `$` (root) or start with `$.`** — bare keys
 * (`'status'`) and joined forms (`'$status'`) are not legal MySQL JSON paths.
 * The extractor enforces this and returns `null` when the path argument fails
 * the contract, so we don't offer enum-value completions in syntactically
 * invalid positions.
 *
 * Pure text scanner — no tree-sitter dependency. Mirrors the backward-scan
 * pattern of `extractJsonPathFromCursor.pgsql.ts`.
 */

import { normalizeJsonPath } from '@/features/workspace/common/utils/jsonFilterSql';
import {
  skipValueOperand,
  COMPARISON_OPERATORS,
  endsWithAt,
} from '../context/comparisonScanHelpers';

export type ExtractJsonPathResult = {
  /** Already passed through normalizeJsonPath. */
  readonly canonicalPath: string;
  /** The column name (unquoted, case-preserved) from the LHS. */
  readonly column: string;
};

/**
 * Returns the normalized JSON path when the cursor sits at the comparison
 * RHS of a MySQL JSON-extract expression, or `null` otherwise.
 *
 * `column` is the column name (folded by the caller per quoting rules) — used
 * to verify the extract expression targets the same column so we don't offer
 * completions for the wrong column.
 */
export function extractJsonPathFromCursorMysql(args: {
  readonly text: string;
  readonly cursorOffset: number;
  /** Omit to accept whichever column the extract expression names — the
   *  scan itself never depends on it (the JSON-chain enum fallback scans
   *  once, then matches the returned `column` against the snapshot). */
  readonly column?: string;
}): ExtractJsonPathResult | null {
  const { text, cursorOffset, column } = args;
  if (cursorOffset < 0 || cursorOffset > text.length) return null;

  // Walk backward from the cursor past the in-progress string literal to land
  // on the comparison operator boundary (exclusive end of LHS).
  const opBoundary = findComparisonOpEndBefore(text, cursorOffset);
  if (opBoundary === null) return null;

  // Skip whitespace before the operator boundary; the next char is either
  // the closing `'` of the path literal (col->>'$.k' form) or the closing
  // `)` of a JSON_EXTRACT / JSON_VALUE / JSON_UNQUOTE call.
  let i = opBoundary - 1;
  while (i >= 0 && /\s/.test(text[i]!)) i--;
  if (i < 0) return null;

  const ch = text[i]!;
  let extracted: ExtractResult | null = null;

  if (ch === "'") {
    // Closing quote of the `->>` path literal.
    extracted = parseArrowChainEndingAt(text, i);
  } else if (ch === ')') {
    // Closing paren of a function call: JSON_EXTRACT / JSON_VALUE /
    // JSON_UNQUOTE(JSON_EXTRACT(…)).
    extracted = parseFunctionCallEndingAt(text, i);
  }

  if (!extracted) return null;

  // Verify the column matches (case-insensitive).
  if (column !== undefined && extracted.column.toLowerCase() !== column.toLowerCase()) return null;

  // Enforce MySQL path-literal contract: accept the canonical root `$` alone
  // or `$.<anything>`. Reject shapes that merely start with `$` (e.g.
  // `$status` — no dot) which the docs warn about but the old startsWith('$')
  // check let through.
  const trimmed = extracted.rawPath.trim();
  if (trimmed === '' || (trimmed !== '$' && !trimmed.startsWith('$.'))) return null;

  return {
    canonicalPath: normalizeJsonPath(extracted.rawPath),
    column: extracted.column,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Comparison operator boundary scan
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Walk back from `offset` over the optional in-progress RHS string literal,
 * using `skipValueOperand` and return the exclusive-end index of the LHS
 * expression. Returns `null` when no recognized comparison operator
 * precedes the cursor's operand position.
 */
function findComparisonOpEndBefore(text: string, offset: number): number | null {
  const i = skipValueOperand(text, offset - 1);
  if (i < 0) return null;

  const opEnd = i + 1;
  for (const op of COMPARISON_OPERATORS) {
    if (endsWithAt(text, opEnd, op)) {
      return opEnd - op.length;
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Form parsers
// ─────────────────────────────────────────────────────────────────────────────

type ExtractResult = {
  /** Column name with any outer backticks / double quotes stripped. */
  column: string;
  /** Raw path-literal content (trimmed). Caller normalizes. */
  rawPath: string;
};

/**
 * Parse a `col->>'$.path'` chain whose closing `'` sits at `end`.
 * The MySQL `->>` is text-typed (analog of PG `->>`). We only recognize
 * single-hop `->>` against a column ref — MySQL doesn't have PG's
 * chained `->` form for path navigation (the path lives inside the literal).
 */
function parseArrowChainEndingAt(text: string, end: number): ExtractResult | null {
  const lit = readQuotedStringEndingAt(text, end);
  if (!lit) return null;

  // Expect `->>` immediately before the opening quote (whitespace tolerated).
  let j = lit.openIdx - 1;
  while (j >= 0 && /\s/.test(text[j]!)) j--;
  if (j < 0) return null;

  const threeBack = text.slice(Math.max(0, j - 2), j + 1);
  if (!threeBack.endsWith('->>')) return null;

  // Position before the operator.
  const beforeOp = j - 3;
  const col = readBareIdentBefore(text, beforeOp);
  if (!col) return null;
  return { column: col, rawPath: lit.content };
}

/**
 * Parse a function-call extract expression whose closing `)` sits at `end`.
 * Recognized:
 *   JSON_EXTRACT(col, '$.path')
 *   JSON_VALUE(col, '$.path')
 *   JSON_UNQUOTE(JSON_EXTRACT(col, '$.path'))
 *
 * Strategy: scan backward to the matching `(` of the outer call, read the
 * preceding function name. If it's JSON_UNQUOTE, we expect its single
 * argument to be a JSON_EXTRACT call (parsed recursively). Otherwise we
 * read its two arguments and require the second to be a quoted path literal.
 */
function parseFunctionCallEndingAt(text: string, end: number): ExtractResult | null {
  if (text[end] !== ')') return null;
  // Find the matching `(`.
  const openIdx = findMatchingOpenParen(text, end);
  if (openIdx < 0) return null;

  // Read the function name immediately preceding the `(`.
  let j = openIdx - 1;
  while (j >= 0 && /\s/.test(text[j]!)) j--;
  const fnName = readFunctionNameBefore(text, j + 1);
  if (!fnName) return null;
  const fn = fnName.toUpperCase();

  const innerStart = openIdx + 1;
  const innerEnd = end; // exclusive — `text[end]` is the `)`

  if (fn === 'JSON_UNQUOTE') {
    // Single argument expected: JSON_EXTRACT(...).
    // Trim whitespace around the inner expression and recurse.
    let s = innerStart;
    let e = innerEnd;
    while (s < e && /\s/.test(text[s]!)) s++;
    while (e > s && /\s/.test(text[e - 1]!)) e--;
    if (e <= s || text[e - 1] !== ')') return null;
    return parseFunctionCallEndingAt(text, e - 1);
  }

  if (fn === 'JSON_EXTRACT' || fn === 'JSON_VALUE') {
    // Split args at the top-level `,`.
    const commaIdx = findTopLevelCommaIn(text, innerStart, innerEnd);
    if (commaIdx < 0) return null;

    // First arg: trimmed → column identifier, possibly qualified by alias
    // (`t.metadata`) and/or backtick/double-quoted. We take the last dotted
    // segment as the column name; alias resolution happens in the caller
    // (resolveColumnRefToTable) which knows the bindings.
    let colS = innerStart;
    let colE = commaIdx;
    while (colS < colE && /\s/.test(text[colS]!)) colS++;
    while (colE > colS && /\s/.test(text[colE - 1]!)) colE--;
    if (colE <= colS) return null;
    const colRaw = text.slice(colS, colE);
    const col = lastDottedSegmentBareName(colRaw);
    if (!isPlainIdent(col)) return null;

    // Second arg: trimmed → must be a single quoted string literal.
    let pS = commaIdx + 1;
    let pE = innerEnd;
    while (pS < pE && /\s/.test(text[pS]!)) pS++;
    while (pE > pS && /\s/.test(text[pE - 1]!)) pE--;
    if (pE <= pS) return null;
    if (text[pS] !== "'" || text[pE - 1] !== "'") return null;
    const litRaw = text.slice(pS, pE);
    const content = parseSingleQuotedLiteral(litRaw);
    if (content === null) return null;
    return { column: col, rawPath: content };
  }

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// String / identifier / paren helpers
// ─────────────────────────────────────────────────────────────────────────────

type QuotedString = {
  content: string;
  /** Index of the opening `'`. */
  openIdx: number;
};

/**
 * Read a single-quoted string literal whose closing `'` sits at `end`.
 * MySQL doubles a single quote inside a literal (`''`) just like SQL.
 */
function readQuotedStringEndingAt(text: string, end: number): QuotedString | null {
  if (text[end] !== "'") return null;
  let i = end - 1;
  let content = '';
  while (i >= 0) {
    const ch = text[i]!;
    if (ch === "'") {
      if (i > 0 && text[i - 1] === "'") {
        content = "'" + content;
        i -= 2;
        continue;
      }
      return { content, openIdx: i };
    }
    if (ch === '\n') return null;
    content = ch + content;
    i--;
  }
  return null;
}

/**
 * Find the index of the `(` matching the `)` at `closeIdx`. Honors single-
 * quoted string literals (so a `(` inside a string doesn't fool the matcher).
 * Returns -1 if no matching paren is found.
 */
function findMatchingOpenParen(text: string, closeIdx: number): number {
  let depth = 0;
  let i = closeIdx;
  while (i >= 0) {
    const ch = text[i]!;
    if (ch === "'") {
      // Walk back past the string literal.
      const lit = readQuotedStringEndingAt(text, i);
      if (!lit) return -1;
      i = lit.openIdx - 1;
      continue;
    }
    if (ch === ')') depth++;
    else if (ch === '(') {
      depth--;
      if (depth === 0) return i;
    }
    i--;
  }
  return -1;
}

/**
 * Within `text[startInclusive..endExclusive]`, return the index of the first
 * top-level comma (depth 0, not inside a quoted string), or -1.
 */
function findTopLevelCommaIn(text: string, startInclusive: number, endExclusive: number): number {
  let depth = 0;
  let i = startInclusive;
  while (i < endExclusive) {
    const ch = text[i]!;
    if (ch === "'") {
      // Skip past the string literal forward.
      i++;
      while (i < endExclusive) {
        if (text[i] === "'") {
          if (i + 1 < endExclusive && text[i + 1] === "'") {
            i += 2;
            continue;
          }
          i++;
          break;
        }
        if (text[i] === '\n') return -1;
        i++;
      }
      continue;
    }
    if (ch === '(') depth++;
    else if (ch === ')') {
      if (depth === 0) return -1;
      depth--;
    } else if (ch === ',' && depth === 0) {
      return i;
    }
    i++;
  }
  return -1;
}

/**
 * Read an identifier (bare, backtick-quoted, or ANSI_QUOTES double-quoted)
 * immediately preceding `end` (exclusive). Returns null if none found.
 */
function readBareIdentBefore(text: string, end: number): string | null {
  let j = end;
  while (j >= 0 && /\s/.test(text[j]!)) j--;
  if (j < 0) return null;

  // Backtick-quoted: `metadata`
  if (text[j] === '`') {
    let i = j - 1;
    while (i >= 0 && text[i] !== '`') {
      if (text[i] === '\n') return null;
      i--;
    }
    if (i < 0) return null;
    return text.slice(i + 1, j);
  }

  // Double-quoted: "metadata" (ANSI_QUOTES). Conservative — accept too.
  if (text[j] === '"') {
    let i = j - 1;
    while (i >= 0 && text[i] !== '"') {
      if (text[i] === '\n') return null;
      i--;
    }
    if (i < 0) return null;
    return text.slice(i + 1, j);
  }

  // Bare identifier — letters, digits, underscore. Must not start with a digit.
  const idEnd = j + 1;
  while (j >= 0 && /[A-Za-z0-9_]/.test(text[j]!)) j--;
  if (j + 1 === idEnd) return null;
  const id = text.slice(j + 1, idEnd);
  if (id.length === 0 || /^\d/.test(id)) return null;
  return id;
}

/**
 * Read a function name (uppercase letters/digits/underscores) ending at
 * `endExclusive`. Returns null when no plain identifier is present.
 */
function readFunctionNameBefore(text: string, endExclusive: number): string | null {
  let j = endExclusive - 1;
  while (j >= 0 && /\s/.test(text[j]!)) j--;
  if (j < 0) return null;
  const idEnd = j + 1;
  while (j >= 0 && /[A-Za-z0-9_]/.test(text[j]!)) j--;
  if (j + 1 === idEnd) return null;
  const name = text.slice(j + 1, idEnd);
  if (name.length === 0 || /^\d/.test(name)) return null;
  return name;
}

/**
 * For a possibly-qualified identifier like `t.metadata`, `db.tbl.col`, or
 * `\`metadata\``, return the bare last segment (`metadata`). Quoted segments
 * have their delimiters stripped. The dotted forms are read right-to-left so
 * the last segment dominates.
 */
function lastDottedSegmentBareName(raw: string): string {
  const t = raw.trim();
  if (t === '') return '';
  // Right-most segment: scan back from the end past a quoted ident or a
  // bare ident run, stopping at the first `.` at top level.
  const end = t.length;
  let i = end - 1;
  // Strip whitespace tail.
  while (i >= 0 && /\s/.test(t[i]!)) i--;
  if (i < 0) return '';
  // Two delimited forms: backtick or double-quote.
  if (t[i] === '`' || t[i] === '"') {
    const delim = t[i]!;
    let j = i - 1;
    while (j >= 0 && t[j] !== delim) {
      if (t[j] === '\n') return '';
      j--;
    }
    if (j < 0) return '';
    return t.slice(j + 1, i);
  }
  // Bare segment.
  const segEnd = i + 1;
  while (i >= 0 && /[A-Za-z0-9_]/.test(t[i]!)) i--;
  return t.slice(i + 1, segEnd);
}

/**
 * Whether `s` is a plain MySQL identifier (letters / digits / underscore,
 * not starting with a digit, length > 0).
 */
function isPlainIdent(s: string): boolean {
  return s.length > 0 && /^[A-Za-z_][A-Za-z0-9_]*$/.test(s);
}

/**
 * Parse a single-quoted string literal of the form `'...'`. Returns the
 * content with `''` escapes collapsed, or null on a malformed input.
 */
function parseSingleQuotedLiteral(raw: string): string | null {
  if (raw.length < 2 || raw[0] !== "'" || raw[raw.length - 1] !== "'") return null;
  const body = raw.slice(1, -1);
  // Collapse `''` → `'`. Reject embedded newlines (mirror the backward parser).
  let out = '';
  let i = 0;
  while (i < body.length) {
    const ch = body[i]!;
    if (ch === '\n') return null;
    if (ch === "'") {
      if (i + 1 < body.length && body[i + 1] === "'") {
        out += "'";
        i += 2;
        continue;
      }
      // A lone `'` inside is malformed — but we already required outer quotes
      // to match exactly, so getting here means a stray quote. Reject.
      return null;
    }
    out += ch;
    i++;
  }
  return out;
}
